import { AnimatePresence, motion } from "motion/react"
import { Link } from "react-router-dom"
import { Portal, TextButton, Typography } from "matsuri-ui"
import { css } from "@emotion/react"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { ReactNode, useState } from "react"
import CloseIcon from "@mui/icons-material/Close"
import MenuIcon from "@mui/icons-material/Menu"
import { theme } from "@/theme"
import {
    useCleanerProfile,
    usePendingApprovalRequests
} from "@/hooks/useApprovalRequest"
import { useTranslation } from "@/hooks/useTranslation"
import { useAppVersion } from "@/hooks/useAppVersion"
import { useIsMatsuriCleaner } from "@/hooks/useCompanyType"
import { featureFlags } from "@/utils/featureFlags"

const badgeSize = 12

const BadgeContainer = ({ children }: { children: ReactNode }) => {
    return (
        <div
            css={css`
                position: relative;
            `}
        >
            {children}
        </div>
    )
}

export const Badge = () => {
    return (
        <span
            css={css`
                position: absolute;
                top: -${badgeSize / 2}px;
                right: -${badgeSize / 2}px;
                width: ${badgeSize}px;
                height: ${badgeSize}px;

                background-color: ${theme.palette.intentions.secondary.main};
                border-radius: 50%;
            `}
        />
    )
}

const styles = {
    item: css`
        display: flex;
        justify-content: flex-start;
        padding: 0;
        font-size: 18px;
        border: none;
    `
}

const LinkItem = ({
    label,
    to,
    onClick,
    badgeEnabled
}: {
    label: string
    to: string
    onClick: () => void
    badgeEnabled?: boolean
}) => {
    return (
        <TextButton as={Link} to={to} onClick={onClick} css={styles.item}>
            <BadgeContainer>
                {badgeEnabled && <Badge />}
                {label}
            </BadgeContainer>
        </TextButton>
    )
}

/**
 * staff_code(Payme社員コード)を持つ清掃員にのみ表示する交通費申請メニュー項目。
 * プロフィール取得は本コンポーネントがレンダーされた場合のみ行われる。
 */
const TransportationExpenseMenuItem = ({
    onClick
}: {
    onClick: () => void
}) => {
    const { token } = useAuth()
    const { t } = useTranslation()
    const { data: profile } = useCleanerProfile(token)

    if (!profile?.staffCode) {
        return null
    }

    return (
        <LinkItem
            to="/transportation_expenses"
            label={t("交通費申請")}
            onClick={onClick}
        />
    )
}

export const MobileMenu = () => {
    const [open, setOpen] = useState(false)
    const { logout } = useAuth()

    const { data: pendingApprovalRequests } = usePendingApprovalRequests()

    const pendingApprovalRequestExist =
        (pendingApprovalRequests?.requests.length ?? 0) > 0

    const { t } = useTranslation()
    const appVersion = useAppVersion()
    const isMatsuriCleaner = useIsMatsuriCleaner()

    return (
        <>
            <TextButton
                icon={
                    <BadgeContainer>
                        {pendingApprovalRequestExist && <Badge />}
                        <MenuIcon />
                    </BadgeContainer>
                }
                onClick={() => setOpen(true)}
                css={css`
                    padding: 16px 20px;
                `}
            />

            <Portal node={document.body}>
                <AnimatePresence>
                    {open && (
                        <motion.nav
                            initial={{ opacity: 0, x: "-80px" }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: "-80px" }}
                            transition={{
                                duration: 0.2,
                                ease: "easeOut"
                            }}
                            css={css`
                                position: fixed;
                                top: 0;
                                left: 0;
                                z-index: ${theme.zIndex.appBar};
                                width: 100vw;
                                height: 100vh;
                                height: 100dvh;
                                padding: calc(
                                        16px + env(safe-area-inset-top, 0px)
                                    )
                                    20px 16px 20px;
                                overflow-x: auto;
                                background-color: white;
                            `}
                        >
                            <div
                                css={css`
                                    position: absolute;
                                    top: calc(
                                        16px + env(safe-area-inset-top, 0px)
                                    );
                                    right: 20px;
                                `}
                            >
                                <TextButton
                                    icon={<CloseIcon />}
                                    onClick={() => setOpen(false)}
                                />
                            </div>
                            <div
                                css={css`
                                    display: grid;
                                    gap: 24px;
                                `}
                            >
                                <Typography
                                    variant="h3"
                                    css={css`
                                        margin-bottom: 24px;
                                    `}
                                >
                                    {t("メニュー")}
                                </Typography>

                                <div
                                    css={css`
                                        display: grid;
                                        gap: 16px;
                                    `}
                                >
                                    {[
                                        {
                                            to: "/schedule",
                                            label: t("清掃予定")
                                        },
                                        {
                                            to: "/approval_requests",
                                            label: t("承認リクエスト"),
                                            badgeEnabled:
                                                pendingApprovalRequestExist
                                        },
                                        {
                                            to: "/shift",
                                            label: t("シフト管理")
                                        },
                                        {
                                            to: "/achievements/weekly",
                                            label: t("清掃実績")
                                        }
                                    ].map((item, index) => (
                                        <LinkItem
                                            {...item}
                                            onClick={() => setOpen(false)}
                                            key={index}
                                        />
                                    ))}
                                    {featureFlags.transportationExpense &&
                                        isMatsuriCleaner && (
                                            <TransportationExpenseMenuItem
                                                onClick={() => setOpen(false)}
                                            />
                                        )}
                                </div>

                                <div
                                    css={css`
                                        border-bottom: 1px solid
                                            ${theme.palette.gray[300]};
                                    `}
                                />

                                <LinkItem
                                    to="/settings/language"
                                    label={t("言語設定")}
                                    onClick={() => setOpen(false)}
                                />

                                <TextButton
                                    onClick={() => {
                                        logout()
                                        setOpen(false)
                                    }}
                                    css={styles.item}
                                >
                                    {t("ログアウト")}
                                </TextButton>

                                {appVersion && (
                                    <Typography
                                        variant="caption"
                                        css={css`
                                            margin-top: 16px;
                                            color: ${theme.palette.gray[500]};
                                        `}
                                    >
                                        {`${t("バージョン")}: ${appVersion.version}`}
                                    </Typography>
                                )}
                            </div>
                        </motion.nav>
                    )}
                </AnimatePresence>
            </Portal>
        </>
    )
}
